// UPDATED: Added admin-only gear icon in header to open IncidentEmailSettingsModal
// UPDATED: Removed unread incident red dot indicators and badge feature
// CHANGE: Removed all debug console statements for production
// Incident management interface for Admins, Directors, Managers, and Operators
// Unified page for viewing, filtering, and creating incidents
// Follows the pattern of UserManagement.tsx
// CHANGE: Added read-only mode for director role - can view all incidents but cannot modify status
// CHANGE: Managers can view their own incidents + incidents from their assigned sites, and can modify status
// CHANGE: Operators can view and create their own incidents only
// CHANGE: All roles can create incidents (operators, managers, directors, admins)
// CHANGE: Removed kebab menu - rows are now directly clickable to view details
// CHANGE: Added search functionality for filtering incidents by user name, site, and category
// CHANGE: Added mobile responsive card view with ResponsiveDataView and ResponsiveDataCard
// CHANGE: Added MobileSearchFilters for mobile-optimized search and filtering (consistent with UserManagement)
// CHANGE: Removed result count display on mobile for cleaner interface
// CHANGE: Added SearchBar component to desktop view for consistent search experience across mobile and desktop
// CHANGE: Integrated incident reporting - "Signaler" button opens form modal/bottom sheet
// CHANGE: Removed ReadOnlyBanner message as it was misleading - status modification is restricted by permissions, not device type
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling
// CHANGE: FAB now hides when detail modal or bottom sheets (profile, etc.) are open
// CHANGE: Reset button now only shows when dropdown filters are active (not for search text, which has its own clear X button)
// CHANGE: Removed white container wrapper around desktop filters for visual consistency with other pages
// REFACTOR: Moved direct supabase.from() sites query to getActiveSites() service function
// UX IMPROVEMENTS:
// - Aligned colors with design system (primary-600 instead of red/orange gradient)
// - Added relative date formatting ("Il y a 2h" instead of "14/06/2025 à 09:30")
// - Mobile cards show category as primary label, date as secondary metadata
// - Replaced AlertTriangle icon with status-colored dot in mobile metadata
// - Loading state now uses BouncingDotsLoader for consistent UX
// - EmptyState now includes an icon
// - New incident highlight uses fade-out animation respecting prefers-reduced-motion
// - Implemented column header sorting (Date, Site, Status) aligned with other management pages
// - Removed sort button from ConsolidatedControlBar in favor of clickable table headers
// - Dynamic sorting with visual indicators (ArrowUpDown, ArrowUp, ArrowDown)
// UPDATED: Migrated to server-side pagination with usePaginatedData for better performance
// - Replaced client-side getIncidents() + useMemo filtering with server-side pagination
// - Server-side filtering for sites, categories, and statuses
// - Client-side sorting for 'site' field (relational) with server-side fallback to 'created_at'
// - Integrated infinite scroll for progressive loading
// - Server-side totalCount for accurate item counter
// UPDATED: Incident detail view now uses BottomSheet on mobile for native swipe-to-dismiss UX
// - Desktop: continues using IncidentDetailModal
// - Mobile: uses new IncidentDetailBottomSheet with swipe gesture support
// - Both components share the same props and behavior (status updates, attachments, lightbox)

import { useState, useEffect, useMemo } from 'react';
import { MapPin, AlertTriangle, Calendar, User, Plus, AlertCircle, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, Users, PackageX, Wrench, HelpCircle, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useBottomSheetOptional } from '../../contexts/BottomSheetContext';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import {
  getCategoryColorClass,
  getStatusColorClass,
  getCategoryInfo,
  getStatusInfo,
  INCIDENT_CATEGORIES,
  INCIDENT_STATUSES
} from '../../lib/incidents';
import type { IncidentWithDetails } from '../../lib/incidents';
import { useToast } from '../../hooks/useToast';
import type { FilterOption } from '../ui/FilterDropdown';
import { MobileFilterBottomSheet, type FilterGroup } from '../ui/MobileFilterBottomSheet';
import { MobileSearchFilters } from '../ui/MobileSearchFilters';
import { ConsolidatedControlBar } from '../ui/ConsolidatedControlBar';
import { ResponsiveDataView } from '../ui/ResponsiveDataView';
import { ResponsiveDataCard } from '../ui/ResponsiveDataCard';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { EmptyState } from '../ui/EmptyState';
import { IncidentDetailModal } from './IncidentDetailModal';
import { IncidentFormModal } from '../incidents/IncidentFormModal';
import { IncidentBottomSheet, IncidentDetailBottomSheet } from '../incidents';
import { IncidentEmailSettingsModal } from '../incidents/IncidentEmailSettingsModal';
import { FloatingActionButton } from '../ui/FloatingActionButton';
import { PageHeader } from '../ui/PageHeader';
import { type Site, getFullName, supabase } from '../../lib/supabase';
import { getActiveSites } from '../../lib/sites';
import { formatRelativeDate, formatDateShort } from '../../lib/dateUtils';

type SortField = 'created_at' | 'site' | 'status';

export function IncidentManagement() {
  const { user: currentUser } = useAuth();
  const { isMobile, isDesktop } = useResponsiveLayout();
  const bottomSheetContext = useBottomSheetOptional();
  const [sites, setSites] = useState<Site[]>([]);
  const { showToast } = useToast();
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [newIncidentId, setNewIncidentId] = useState<string | null>(null);

  // Check if current user is director or on mobile (read-only mode)
  const isReadOnly = currentUser?.role === 'director' || isMobile;

  // All authenticated users can create incidents
  const canCreateIncident = !!currentUser;

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Mobile filter state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Detail modal state
  const [viewingIncident, setViewingIncident] = useState<IncidentWithDetails | null>(null);

  // Load sites for filter dropdown
  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    const sitesResult = await getActiveSites();
    if (sitesResult.success && sitesResult.data) {
      setSites(sitesResult.data);
    }
  };

  // Build filters object for server-side filtering
  const filters = useMemo(() => {
    const result: Record<string, unknown> = {};

    if (selectedSites.length > 0) {
      result.site_id = selectedSites;
    }

    if (selectedCategories.length > 0) {
      result.category = selectedCategories;
    }

    if (selectedStatuses.length > 0) {
      result.status = selectedStatuses;
    }

    return result;
  }, [selectedSites, selectedCategories, selectedStatuses]);

  // Use server-side ordering for all fields except 'site' (relational)
  // For 'site', fallback to 'created_at' and apply client-side sorting
  const serverOrderColumn = sortField === 'site' ? 'created_at' : sortField;

  // Use paginated data hook for incidents
  const {
    items: rawIncidents,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  } = usePaginatedData<IncidentWithDetails>({
    supabase,
    table: 'incidents',
    pageSize: 20,
    orderBy: { column: serverOrderColumn, ascending: sortOrder === 'asc' },
    filters,
    searchColumn: 'description',
    searchTerm,
    select: '*, user:users!incidents_user_id_fkey(id, username, first_name, last_name, role), site:sites!incidents_site_id_fkey(id, name), resolved_by_user:users!incidents_resolved_by_fkey(id, username, first_name, last_name)',
  });

  // Apply client-side sorting for 'site' field (relational)
  const incidents = useMemo(() => {
    if (sortField !== 'site') {
      return rawIncidents;
    }

    // Client-side sorting by site name
    return [...rawIncidents].sort((a, b) => {
      const comparison = (a.site?.name || '').localeCompare(b.site?.name || '');
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [rawIncidents, sortField, sortOrder]);

  const handleViewIncident = (incident: IncidentWithDetails) => {
    setViewingIncident(incident);
  };

  const handleCloseDetail = () => {
    setViewingIncident(null);
    refresh(); // Refresh data in case status was updated
  };

  const handleOpenIncidentForm = () => {
    setShowIncidentForm(true);
  };

  const handleCloseIncidentForm = () => {
    setShowIncidentForm(false);
  };

  const handleIncidentCreated = async () => {
    showToast('Incident signalé avec succès', 'success');
    // Reload incidents to show the new one
    await refresh();
    // Get the most recent incident ID (first in the list after reload)
    // We'll set it to highlight temporarily
    if (incidents.length > 0) {
      setNewIncidentId(incidents[0].id);
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setNewIncidentId(null);
      }, 3000);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} className="text-gray-400" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp size={16} className="text-primary-600" />
      : <ArrowDown size={16} className="text-primary-600" />;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      client: <Users size={16} className="text-gray-500" />,
      stock: <PackageX size={16} className="text-gray-500" />,
      equipment: <Wrench size={16} className="text-gray-500" />,
      other: <HelpCircle size={16} className="text-gray-500" />
    };
    return icons[category] || <HelpCircle size={16} className="text-gray-500" />;
  };

  // Filter options (no counts to avoid loading all data)
  const siteFilterOptions: FilterOption[] = sites.map(site => ({
    value: site.id,
    label: site.name,
  }));

  const categoryFilterOptions: FilterOption[] = INCIDENT_CATEGORIES.map(category => ({
    value: category.value,
    label: category.label,
  }));

  const statusFilterOptions: FilterOption[] = INCIDENT_STATUSES.map(status => ({
    value: status.value,
    label: status.label,
  }));

  const handleClearFilters = () => {
    setSelectedSites([]);
    setSelectedCategories([]);
    setSelectedStatuses([]);
  };

  const hasActiveFilters = selectedSites.length > 0 ||
    selectedCategories.length > 0 || selectedStatuses.length > 0;
  const hasAnyCriteria = searchTerm.trim() !== '' || hasActiveFilters;

  // Use totalCount from server for accurate count
  const displayCount = totalCount ?? incidents.length;

  // Get status icon color class for mobile metadata
  const getStatusDotColor = (status: string): string => {
    const colors: Record<string, string> = {
      open: 'bg-blue-500',
      in_progress: 'bg-yellow-500',
      resolved: 'bg-green-500',
      closed: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  // Mobile filter groups - Hide site filter for operators
  const mobileFilterGroups: FilterGroup[] = [
    ...(currentUser?.role !== 'operator' ? [{
      id: 'site',
      label: 'Site',
      options: siteFilterOptions,
      selectedValues: selectedSites,
    }] : []),
    {
      id: 'category',
      label: 'Thématique',
      options: categoryFilterOptions,
      selectedValues: selectedCategories,
    },
    {
      id: 'status',
      label: 'Statut',
      options: statusFilterOptions,
      selectedValues: selectedStatuses,
    },
  ];

  const handleMobileFilterChange = (groupId: string, values: string[]) => {
    if (groupId === 'site') {
      setSelectedSites(values);
    } else if (groupId === 'category') {
      setSelectedCategories(values);
    } else if (groupId === 'status') {
      setSelectedStatuses(values);
    }
  };

  const handleClearAllMobileFilters = () => {
    setSelectedSites([]);
    setSelectedCategories([]);
    setSelectedStatuses([]);
  };

  const activeFilterCount = selectedSites.length + selectedCategories.length + selectedStatuses.length;

  // Render incident card for mobile view
  const renderIncidentCard = (incident: IncidentWithDetails) => {
    const categoryInfo = getCategoryInfo(incident.category);

    return (
      <div
        onClick={() => handleViewIncident(incident)}
        className="cursor-pointer"
      >
        <ResponsiveDataCard
          primaryLabel={categoryInfo?.label || 'Inconnu'}
          secondaryLabel={getFullName(incident.user)}
          badge={{
            label: getStatusInfo(incident.status)?.label || 'Inconnu',
            colorClass: getStatusColorClass(incident.status),
          }}
          metadata={[
            {
              icon: <MapPin size={16} className="text-gray-400" />,
              value: incident.site.name,
            },
            {
              icon: <span className={`w-2 h-2 rounded-full ${getStatusDotColor(incident.status)}`} />,
              value: formatRelativeDate(incident.created_at),
            },
          ]}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <PageHeader title="Incidents" isMobile={isMobile}>
        <div className="flex items-center gap-2">
          {currentUser?.role === 'admin' && (
            <button
              onClick={() => setShowEmailSettings(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
              title="Parametrage des notifications email"
              aria-label="Parametrage des notifications email"
            >
              <Settings size={20} />
            </button>
          )}
          {canCreateIncident && isDesktop && (
            <button
              onClick={handleOpenIncidentForm}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus size={18} />
              <span>Signaler un incident</span>
            </button>
          )}
        </div>
      </PageHeader>

      {/* Desktop Filters */}
      {isDesktop && (
        <ConsolidatedControlBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Rechercher un incident..."
          itemCount={displayCount}
          itemLabel="incident"
          filters={[
            ...(currentUser?.role !== 'operator' ? [{
              label: 'Site',
              options: siteFilterOptions,
              selectedValues: selectedSites,
              onChange: setSelectedSites,
            }] : []),
            {
              label: 'Thématique',
              options: categoryFilterOptions,
              selectedValues: selectedCategories,
              onChange: setSelectedCategories,
            },
            {
              label: 'Statut',
              options: statusFilterOptions,
              selectedValues: selectedStatuses,
              onChange: setSelectedStatuses,
            },
          ]}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          showSort={false}
        />
      )}

      {/* Mobile Search and Filters */}
      {isMobile && (
        <MobileSearchFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterClick={() => setShowMobileFilters(true)}
          activeFilterCount={activeFilterCount}
          showFilterButton={true}
          searchPlaceholder="Rechercher un incident..."
        />
      )}

      {/* Incidents table/cards */}
      {incidents.length === 0 && !isLoading ? (
        <EmptyState
          message={hasActiveFilters ? 'Aucun incident ne correspond à vos critères' : 'Tout est en ordre ! Aucun incident n\'a été signalé.'}
          actionLabel={hasActiveFilters ? 'Réinitialiser les filtres' : 'Signaler un incident'}
          onAction={hasActiveFilters ? handleClearFilters : handleOpenIncidentForm}
          icon={hasActiveFilters ? <AlertCircle size={48} className="text-gray-300" /> : <CheckCircle size={48} className="text-green-300" />}
        />
      ) : (
        <ResponsiveDataView
          items={incidents}
          isDesktop={isDesktop}
          cardRenderer={renderIncidentCard}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          tableSkeletonComponent={
            <div className="flex items-center justify-center min-h-[400px]">
              <BouncingDotsLoader size="lg" />
            </div>
          }
          tableComponent={
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('created_at')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>DATE</span>
                          {getSortIcon('created_at')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        UTILISATEUR
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('site')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>SITE</span>
                          {getSortIcon('site')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        THÉMATIQUE
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('status')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>STATUT</span>
                          {getSortIcon('status')}
                        </button>
                      </th>
                      <th className="w-10 px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {incidents.map((incident) => {
                      const categoryInfo = getCategoryInfo(incident.category);
                      const statusInfo = getStatusInfo(incident.status);

                      const isNewIncident = newIncidentId === incident.id;

                      return (
                        <tr
                          key={incident.id}
                          onClick={() => handleViewIncident(incident)}
                          className={`group hover:bg-gray-50 transition-all cursor-pointer ${
                            isNewIncident ? 'ring-2 ring-primary-400 bg-primary-50 animate-[fadeOut_3s_ease-out_forwards] motion-reduce:animate-none motion-reduce:bg-primary-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {formatDateShort(incident.created_at)}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="font-medium text-gray-900">{getFullName(incident.user)}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {incident.site.name}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(incident.category)}
                              <span className="text-sm text-gray-700">{categoryInfo?.label}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${getStatusDotColor(incident.status)}`} />
                              <span className="text-sm text-gray-700">{statusInfo?.label}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <ChevronRight size={16} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}

      {/* Detail Modal - Desktop uses Modal, Mobile uses BottomSheet */}
      {viewingIncident && (
        <>
          {isDesktop ? (
            <IncidentDetailModal
              incident={viewingIncident}
              onClose={handleCloseDetail}
            />
          ) : (
            <IncidentDetailBottomSheet
              incident={viewingIncident}
              onClose={handleCloseDetail}
            />
          )}
        </>
      )}

      {/* Mobile filter bottom sheet */}
      <MobileFilterBottomSheet
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filterGroups={mobileFilterGroups}
        onChange={handleMobileFilterChange}
        onClearAll={handleClearAllMobileFilters}
      />

      {/* Incident Form - Modal on desktop, Bottom Sheet on mobile */}
      {canCreateIncident && (
        <>
          {isDesktop ? (
            <IncidentFormModal
              isOpen={showIncidentForm}
              onClose={handleCloseIncidentForm}
              onSuccess={handleIncidentCreated}
            />
          ) : (
            <IncidentBottomSheet
              isOpen={showIncidentForm}
              onClose={handleCloseIncidentForm}
              onSuccess={handleIncidentCreated}
            />
          )}
        </>
      )}

      {/* Email Settings Modal - Admin only */}
      {currentUser?.role === 'admin' && (
        <IncidentEmailSettingsModal
          isOpen={showEmailSettings}
          onClose={() => setShowEmailSettings(false)}
        />
      )}

      {/* Floating Action Button for mobile - Only for operators and managers */}
      {canCreateIncident && isMobile && !showIncidentForm && !viewingIncident && !bottomSheetContext?.isAnySheetOpen && (
        <FloatingActionButton
          onClick={handleOpenIncidentForm}
          icon={<Plus size={24} strokeWidth={2.5} />}
          ariaLabel="Signaler un incident"
          title="Signaler un incident"
        />
      )}
    </div>
  );
}
